// src/components/AIAgent/AgentHealthStatus.jsx

import React, { useEffect, useState } from "react";
import { useAIAgentStore } from "../../services/useAIAgentStore";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import "./AgentHealthStatus.css";

const AgentHealthStatus = ({ autoRefresh = false }) => {
  const { healthCheck, fetchHealthCheck } = useAIAgentStore();
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadHealthCheck();

    if (autoRefresh) {
      const interval = setInterval(loadHealthCheck, 60000); // Refresh every minute
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadHealthCheck = async () => {
    setLoading(true);
    try {
      await fetchHealthCheck();
    } catch (error) {
      console.error("Error fetching health check:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !healthCheck) {
    return (
      <div className="agent-health-status loading">
        <FaSyncAlt className="spin" /> Verificando status do agente...
      </div>
    );
  }

  if (!healthCheck) return null;

  const { is_ready, checks, errors, warnings, summary } = healthCheck;

  return (
    <div className={`agent-health-status ${is_ready ? "ready" : "not-ready"}`}>
      {/* Header - Always visible */}
      <div className="health-header" onClick={() => setExpanded(!expanded)}>
        <div className="health-title">
          {is_ready ? (
            <>
              <FaCheckCircle className="status-icon success" />
              <span>Agente Pronto para Atender</span>
            </>
          ) : (
            <>
              <FaTimesCircle className="status-icon error" />
              <span>Agente NAO Esta Pronto</span>
            </>
          )}
        </div>

        <div className="health-actions">
          <button
            className="btn-refresh"
            onClick={(e) => {
              e.stopPropagation();
              loadHealthCheck();
            }}
            disabled={loading}
          >
            <FaSyncAlt className={loading ? "spin" : ""} />
          </button>
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </div>

      {/* Summary Badge */}
      {summary && (
        <div className="health-summary">
          {summary.errors > 0 && (
            <span className="badge error">{summary.errors} erros</span>
          )}
          {summary.warnings > 0 && (
            <span className="badge warning">{summary.warnings} avisos</span>
          )}
          {summary.total_checks > 0 && (
            <span className="badge success">{summary.total_checks} OK</span>
          )}
        </div>
      )}

      {/* Expandable Content */}
      {expanded && (
        <div className="health-content">
          {/* Errors - Critical issues that BLOCK functionality */}
          {errors && errors.length > 0 && (
            <div className="health-section errors">
              <h4>Problemas que IMPEDEM o agente de responder:</h4>
              {errors.map((err, idx) => (
                <div key={idx} className="health-item error">
                  <FaTimesCircle className="item-icon" />
                  <div className="item-content">
                    <strong>{err.message}</strong>
                    <p className="item-action">{err.action}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Warnings - Non-blocking but important */}
          {warnings && warnings.length > 0 && (
            <div className="health-section warnings">
              <h4>Recomendacoes:</h4>
              {warnings.map((warn, idx) => (
                <div key={idx} className="health-item warning">
                  <FaExclamationTriangle className="item-icon" />
                  <div className="item-content">
                    <strong>{warn.message}</strong>
                    <p className="item-action">{warn.action}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Checks OK */}
          {checks && checks.length > 0 && (
            <div className="health-section checks">
              <h4>Configuracoes OK:</h4>
              <div className="checks-grid">
                {checks.map((check, idx) => (
                  <div key={idx} className="health-item check">
                    <FaCheckCircle className="item-icon" />
                    <span>{check.name}</span>
                    {check.count !== undefined && (
                      <span className="count">({check.count})</span>
                    )}
                    {check.products !== undefined && (
                      <span className="count">
                        ({check.products} prod, {check.services} serv)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentHealthStatus;
